/**
 * Console and Network Error Capture
 *
 * Bridges contextCapture callbacks to the errorLogging system.
 * contextCapture handles the monkey-patching and buffering;
 * this module decides which events are worth reporting to Firebase.
 */

import * as Sentry from '@sentry/react';
import { captureError, wasReportedToSentry } from './captureError';
import { addBreadcrumb } from './breadcrumbs';
import { isDraftSyncTransientNetworkFailure } from '../../forms/draftSyncNetworkPolicy';
import { activeFormTagsForUrl } from '../../forms/activeFormContext';
import { recordTransportFailure } from './transportFailureCounter';
import { recordVersionPollFailure, recordVersionPollSuccess } from './versionPollStreak';
import { redactForErrorReport } from '../../contextCapture/sanitizer';
import { reportCallableHttpRejection } from '../../telemetry/reportCallableHttpRejection';
import type { HedgeOutcome } from '../../callableRetry';
import { wakeContextFor } from './wakeContext';
import { deferToRetry, setDeferredFailureReporter } from './retryWindow';

// ── Throttling / Dedup ──────────────────────────────────────────────────

const MAX_ERRORS_PER_MINUTE = 10;
const DEDUP_WINDOW_MS = 60_000;

interface ThrottleState {
  readonly recentlySent: Map<string, number>;
  countThisMinute: number;
  lastMinuteReset: number;
}

const state: ThrottleState = {
  recentlySent: new Map<string, number>(),
  countThisMinute: 0,
  lastMinuteReset: Date.now(),
};

function fingerprint(msg: string): string {
  return msg.substring(0, 100).replace(/\s+/g, ' ').trim();
}

function shouldReport(message: string): boolean {
  const now = Date.now();
  const fp = fingerprint(message);

  if (now - state.lastMinuteReset > 60_000) {
    state.countThisMinute = 0;
    state.lastMinuteReset = now;
    // Prune stale entries
    state.recentlySent.forEach((ts, key) => {
      if (now - ts > DEDUP_WINDOW_MS) state.recentlySent.delete(key);
    });
  }

  if (state.countThisMinute >= MAX_ERRORS_PER_MINUTE) return false;
  const last = state.recentlySent.get(fp);
  if (last && now - last < DEDUP_WINDOW_MS) return false;

  return true;
}

/**
 * Test-only: restore pristine throttle state so test files cannot couple via
 * order. Mirrors `__resetDraftSyncNetworkPolicyForTests`.
 *
 * Without this, MAX_ERRORS_PER_MINUTE (10) is a SHARED BUDGET across every test
 * in a file: each assertion that expects a report consumes one, and once the
 * budget is spent `shouldReport` returns false, so a later test sees zero
 * captures and reads as a broken feature rather than an exhausted counter. The
 * failure is silent and points at the wrong thing, which is why this is
 * exported rather than left to whoever hits it next.
 */
export function __resetErrorThrottleForTests(): void {
  state.countThisMinute = 0;
  state.lastMinuteReset = Date.now();
  state.recentlySent.clear();
}

function markReported(message: string): void {
  state.recentlySent.set(fingerprint(message), Date.now());
  state.countThisMinute++;
}

// ── Error-pattern filter ────────────────────────────────────────────────

const ERROR_PATTERNS = [
  /error/i, /exception/i, /failed/i, /cannot/i, /unable/i,
  /undefined is not/i, /null is not/i, /is not a function/i,
  /is not defined/i, /network/i, /timeout/i, /firebase/i,
  /firestore/i, /permission/i, /unauthorized/i, /invalid/i,
];

const IGNORE_PATTERNS = [
  /Failed to log error to Firebase/i,
  /Download the React DevTools/i,
  /Warning: ReactDOM.render is no longer supported/i,
  /StrictMode/i,
  /componentWillMount has been renamed/i,
  /componentWillReceiveProps has been renamed/i,
  // Firestore SDK internal — handled by switching to memoryLocalCache
  /Failed to obtain primary lease/i,
  /Detected an update time that is in the future/i,
  // Firestore SDK internal — its own "went offline" notice. The SDK switches to
  // offline mode and reconnects by itself; the line says so in its own text. Seven
  // events in two weeks, zero users blocked (OFFICEHUB-CLIENT-5X). A Firestore
  // outage that matters still surfaces through the reads and writes that fail.
  /Could not reach Cloud Firestore backend/i,
  // Google Maps' own notice that the browser cannot run the WebGL vector map. The SDK
  // draws the same map as raster tiles and says so in its own text; nobody is blocked.
  // Nine events, one iPhone user (OFFICEHUB-CLIENT-6W); the WebGL cause is inferred from
  // the notice's own link, not measured. A map that fails to load at all
  // is a different line ("could not load") and still reports.
  /Attempted to load a Vector Map, but failed\. Falling back to Raster/i,
];

/**
 * A DESIGNED REFUSAL: a callable that said no on purpose, in a sentence written for the person at the screen, that
 * the screen already shows (OFFICEHUB groups 0deee7d and f91bc0b). Logging it as a client error buried real faults in
 * noise and re-opened the same group each time.
 *
 * AN ALLOWLIST OF EXACT SENTENCES, never a class. "Any permission-denied with a sentence" would also silence real
 * regressions ("Only admins and managers can ...", "Not a member", "User does not have access to this organization")
 * and configuration faults ("GITHUB_TOKEN secret is not configured."), because a server writes a sentence for those
 * too. So an entry here is added only after someone has read the group and confirmed the refusal is the intended
 * behaviour. Each entry pins the code AND the whole sentence (anchored); a near miss, another code, or the same words
 * with extra text all keep reporting to error_groups and Sentry.
 *
 * Also true of every entry: only through the console intercept (a caller that caught the error and logged it; an
 * unhandled rejection of the same error is still a bug), and only for an Error object carrying the code.
 * What is kept: a `handled_refusal` breadcrumb that rides along with any LATER event from the same session. It is not
 * stored on its own, so a refusal with no later event leaves no record anywhere; that is the accepted cost.
 *
 * Sources of the sentences: functions/src/forms-module/services/formSubmissionService.ts (STARTED_ON_BEHALF_MESSAGE;
 * the "Only a submitted report can be reviewed" failed-precondition, whose status slot is the only variable part).
 */
interface DesignedRefusal {
  readonly code: string;
  /** Anchored: the whole message, optionally followed by the transport's own " [403]" style status tag. */
  readonly sentence: RegExp;
}

const DESIGNED_REFUSALS: readonly DesignedRefusal[] = [
  {
    code: 'functions/permission-denied',
    sentence:
      /^You started this report on the technician's behalf and cannot also approve it\. Ask another manager to review it\.(?: \[\d{3}\])?$/,
  },
  {
    code: 'functions/failed-precondition',
    sentence:
      /^Only a submitted report can be reviewed \(this one is "[A-Za-z_ -]{1,30}"\)\. To correct the CONTENTS of a report that was already decided, open an amendment\. Reversing the decision itself is not self-service \u2014 ask an administrator\.(?: \[\d{3}\])?$/,
  },
];

export function isDesignedRefusal(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (typeof code !== 'string' || typeof message !== 'string') return false;
  return DESIGNED_REFUSALS.some((r) => r.code === code && r.sentence.test(message.trim()));
}

function isReportable(message: string): boolean {
  if (IGNORE_PATTERNS.some(p => p.test(message))) return false;
  return ERROR_PATTERNS.some(p => p.test(message));
}

/**
 * Strip the Firebase SDK logger's leading ISO-8601 timestamp.
 *
 * `@firebase/logger` prefixes every console line with its own timestamp, so a
 * message that recurs is a NEW string every time. That defeats three things at
 * once, and all three were live (#3777):
 *
 *   1. the 60s dedupe above - it keys on `msg.substring(0, 100)`, and the prefix
 *      sits inside those first 100 chars, so the key never repeats and the
 *      window never fires. One user produced 120 b815 events across 4 sessions.
 *   2. Sentry grouping - `captureError` hands the SAME Error object to
 *      `Sentry.captureException`, so each occurrence became its own issue.
 *   3. the `error_events` fingerprint - `generateFingerprint` hashes the message.
 *
 * Nothing is lost: Sentry stamps its own receive time and `error_events` stores a
 * `timestamp` field, both within milliseconds of the SDK's. Only the LEADING
 * bracketed prefix is removed - a timestamp mid-message is left alone, since
 * there it may be meaningful payload rather than log chrome.
 */
const SDK_TIMESTAMP_PREFIX =
  /^\s*\[\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?\]\s*/;

export function stripSdkTimestamp(message: string): string {
  return message.replace(SDK_TIMESTAMP_PREFIX, '');
}

// ── Public callbacks (wired into contextCapture) ────────────────────────

/**
 * Called by contextCapture when a console.error or console.warn fires.
 * Decides whether the message warrants a Firebase error report.
 *
 * `originalError` is the first `Error` instance among the console call's
 * arguments, when there was one. It is what makes this report honest, and it
 * does two distinct jobs:
 *
 *  1. IDENTITY. Reporting `new Error(message)` built here gives the event THIS
 *     file's stack, so our own logging code becomes the "most relevant frame",
 *     the real throw site is lost, and unrelated crashes collapse into one
 *     frame-grouped bucket. Live proof in `.github/sweep-data/actionable-errors.json`:
 *     4 of 16 captured client errors have `Object.d [as onConsoleError]` as their
 *     top frame — including `FirebaseError: Invalid phone number`, thrown from an
 *     `onClick` two frames down — and the same 4 carry that as `functionName` in
 *     the `error_events` record the triage UI reads. Passing the real error fixes
 *     BOTH sinks, because `captureError` derives stack/file/line/functionName
 *     from the object it is handed.
 *  2. EXACTLY-ONCE. An error boundary reports the crash directly and then
 *     `console.error`s it; that second pass arrives here. `wasReportedToSentry`
 *     recognises the same object and drops the duplicate instead of re-reporting
 *     it with a worse stack.
 *
 * Both are additive: with no Error argument the behaviour is exactly as before,
 * plus an explicit fingerprint so distinct messages stop sharing one issue.
 */
export function handleConsoleError(
  message: string,
  level: 'error' | 'warning',
  originalError?: Error
): void {
  if (level === 'warning') return; // Don't auto-report warnings

  // This exact throw already reached Sentry through a direct captureError call
  // (an error boundary reporting, then console.error-ing, the same crash).
  // Reporting it again would add a SECOND event carrying this file's stack.
  //
  // Deliberately narrow: it drops a duplicate DELIVERY of one error object, not
  // an error CLASS. Nothing is suppressed by message, signature or rate here —
  // a second, genuinely separate crash of the same kind is a different object
  // and still reports.
  if (originalError && wasReportedToSentry(originalError)) return;

  // A designed refusal on the allowlist that the caller caught and logged: the person was told, nothing is broken.
  // Not reported; a breadcrumb rides along with any later event of this session (see DESIGNED_REFUSALS).
  if (isDesignedRefusal(originalError)) {
    addBreadcrumb('console', 'handled_refusal', stripSdkTimestamp(message).slice(0, 200), {
      code: (originalError as { code?: string }).code,
    });
    return;
  }

  // Normalise BEFORE the dedupe check - see stripSdkTimestamp. Doing it after
  // would leave the dedupe keyed on a string that changes every occurrence.
  const normalised = stripSdkTimestamp(message);

  if (!isReportable(normalised)) return;
  if (!shouldReport(normalised)) return;

  markReported(normalised);

  captureError({
    // The real error when we have one; the message-derived Error only as a
    // fallback, for `console.error('some string')` with no Error argument.
    error: originalError ?? new Error(normalised),
    severity: level,
    category: 'console_error',
    capturePoint: 'console_intercept',
    // Only the fallback needs an explicit fingerprint. A real error has a real
    // stack, and Sentry's default grouping on that is correct — and better than
    // anything a message hash could express. The fallback has no stack worth
    // grouping on (it is all this file), so group it by the same normalised
    // message key the 60s dedupe already uses: identical messages group,
    // different messages do not.
    ...(originalError ? {} : { fingerprint: ['console_intercept', fingerprint(normalised)] }),
    additionalContext: {
      // The formatted console line, kept whether or not it became the message.
      // `console.error('Failed to send text:', err)` carries context in the
      // prefix that the Error object alone does not.
      consoleMessage: normalised,
    },
  });
}

/**
 * URLs that should never trigger a Firebase error report from the network
 * interceptor, because they represent "normal" fetch failures that aren't
 * actionable and would flood error_events if reported.
 *
 * Firestore's Listen channel is a long-polling fetch that the SDK closes
 * and reopens constantly (on tab switch, idle, reconnect, unsubscribe).
 * Each close surfaces to the wrapped fetch as status=0 "network failure",
 * but the SDK handles its own reconnect logic — there's nothing for the
 * app (or the rep) to do. Between 2026-04-04 and 2026-04-08 this single
 * pattern generated ~13,000 error_events (>90% of total volume) and
 * buried real errors in the noise.
 *
 * Similarly, any request to `firestore.googleapis.com` is managed by the
 * Firestore SDK's own retry logic — we never add value by duplicating it.
 */
const IGNORED_ERROR_URLS: readonly RegExp[] = [
  /firestore\.googleapis\.com/i,
  /firebasestorage\.googleapis\.com/i,
  /identitytoolkit\.googleapis\.com/i,
  /securetoken\.googleapis\.com/i,
  // Firebase Installations / Remote Config also have their own retry logic
  /firebaseinstallations\.googleapis\.com/i,
  /firebaseremoteconfig\.googleapis\.com/i,
  // Fire-and-forget telemetry — caller already uses .catch(() => {}),
  // network failures are expected and harmless
  /logPerformanceMetric/i,
  // Google's own telemetry beacon, fired by the apis.google.com script (not by
  // app code). Routinely killed by ad blockers and flaky networks; its failure
  // carries zero app signal, and nothing in this repo issues or consumes it
  // (OFFICEHUB-CLIENT-59). Scoped to the gen_204 ping only — a failure loading
  // an actual Google API script would matter, but script tags don't pass
  // through this fetch interceptor anyway.
  /apis\.google\.com\/js\/gen_204/i,
];

function isIgnoredUrl(url: string): boolean {
  return IGNORED_ERROR_URLS.some((pattern) => pattern.test(url));
}

/**
 * Fire-and-forget telemetry callables whose own callers already handle failure.
 *
 * These are NOT in IGNORED_ERROR_URLS on purpose. That list suppresses a URL at
 * EVERY status, which is right for an SDK endpoint we never call directly but
 * wrong here: a persistent HTTP 5xx from one of these means the telemetry
 * function itself is broken, and that is worth knowing. The noise is entirely
 * status-0 — a dropped client connection — so the suppression is scoped to it.
 *
 * - `logUsageEvent`: usageTracking.trackEvent ends in `.catch(recordFailure)`.
 *   #4022 deliberately made the drop COUNTABLE via lib/telemetry/deliveryFailures,
 *   so a lost analytics beacon is already accounted for in the place designed to
 *   account for it; reporting it again as an error adds no signal.
 * - `logError`: the error-reporting transport itself (captureError.ts). Its own
 *   failure handler uses `console.warn` rather than `console.error` expressly
 *   "to avoid triggering window.onerror -> captureError loop" — but the fetch
 *   interceptor sits below that choice and reported it anyway, re-creating the
 *   recursion that comment exists to prevent.
 *
 * `logPerformanceMetric` stays in IGNORED_ERROR_URLS (all statuses) rather than
 * moving here. Narrowing it would START reporting 5xx that are currently silent
 * — a behaviour change with its own blast radius, which belongs in its own
 * change rather than riding along with this one.
 *
 * 🔴 `forms_logError` is DELIBERATELY NOT IN THIS LIST, even though it is also
 * fire-and-forget. The two above are safe to drop because the loss is accounted
 * for somewhere else — usageTracking counts it, and captureError has already
 * handed the error to Sentry before it ever POSTs. `forms_logError` has neither:
 * useFormErrorLogger.ts:166 ends in a bare `.catch(() => {})` with no counter,
 * and `form_error_logs` is the instrument the FSR pain work reads. Suppressing
 * it would make a LOST pain signal indistinguishable from NO pain — the precise
 * failure this file's other comments exist to prevent.
 *
 * That is why both patterns are anchored on a leading `/` and a URL-end
 * boundary: an unanchored /logError/i also matches `/forms_logError`, which
 * would have silently swallowed exactly the case that must stay visible.
 *
 * They are ALSO host-scoped to this project's Cloud Functions origin, so a
 * third-party URL that merely ends in `/logError` cannot borrow the
 * suppression. The region segment is a wildcard on purpose: callables are
 * split across us-central1 and us-west1 (see getFunctionsForCallable in
 * config/firebase.ts), so pinning one region would silently stop matching if a
 * beacon moved. There is no shared functions-origin constant to reuse — the
 * literal is duplicated at ~15 call sites in client/src — and extracting one
 * is a separate change, not cargo for this PR.
 *
 * Failure direction is deliberate: if the origin ever changes (a custom domain
 * via REACT_APP_FUNCTIONS_URL, a project rename), these stop matching and the
 * beacons become NOISY again. That is fail-open toward noise, never toward
 * silence — the safe direction for a suppression rule.
 */
const FIRE_AND_FORGET_TELEMETRY_URLS: readonly RegExp[] = [
  /\/\/[a-z0-9-]+-quoter-saas\.cloudfunctions\.net\/logUsageEvent(?:[?#]|$)/i,
  /\/\/[a-z0-9-]+-quoter-saas\.cloudfunctions\.net\/logError(?:[?#]|$)/i,
];

/**
 * True when a telemetry beacon failed at the network layer (status 0) — the
 * caller already handles it and counts it. Every other status, including every
 * 5xx, still reports: that is the control which keeps a genuinely broken
 * telemetry function visible (OFFICEHUB-CLIENT-2 / -J).
 */
function isFireAndForgetTelemetryNetworkFailure(url: string, status: number): boolean {
  if (status !== 0) return false;
  return FIRE_AND_FORGET_TELEMETRY_URLS.some((pattern) => pattern.test(url));
}

/**
 * Best-effort third-party lookups whose callers already fall back on ANY failure.
 *
 * Nominatim reverse geocoding: both callers (the GPS form field and
 * lib/survey/reverseGeocode.ts) return null on failure and the user types the
 * address instead, so a request that never got a response (status 0: flaky field
 * signal, the third party dropping it) is expected and carries no app signal
 * (OFFICEHUB-CLIENT-6H). Same shape as the telemetry list above: status-0 only,
 * so a Nominatim 5xx still reports, and anchored on scheme + exact host + path, so
 * a lookalike host or another Nominatim endpoint cannot borrow the suppression.
 */
const BEST_EFFORT_THIRD_PARTY_URLS: readonly RegExp[] = [
  /^https:\/\/nominatim\.openstreetmap\.org\/reverse(?:[?#]|$)/i,
];

/**
 * Hosts whose full URL (query string included) we keep in a network report: our
 * own app hosts and our Cloud Functions origin. Their query strings are ours and
 * the report's dedupe key and existing triage depend on them.
 */
const FIRST_PARTY_HOSTS: readonly RegExp[] = [
  /^(?:[a-z0-9-]+\.)*onpointgen\.com$/i,
  /^[a-z0-9-]+-quoter-saas\.cloudfunctions\.net$/i,
];

/**
 * The URL as it may appear in a network error report. A THIRD party's query
 * string is dropped: it can carry the device's GPS (Nominatim's lat/lon) or a
 * user's search term, and none of it is ours to store. First-party URLs, the
 * page's own host and anything unparseable are returned unchanged.
 */
export function reportableUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
  } catch {
    return url;
  }
  const host = parsed.hostname;
  const isOwnPage = typeof window !== 'undefined' && parsed.host === window.location.host;
  if (isOwnPage || FIRST_PARTY_HOSTS.some((pattern) => pattern.test(host))) return url;
  return `${parsed.origin}${parsed.pathname}`;
}

function isBestEffortThirdPartyNetworkFailure(url: string, status: number): boolean {
  if (status !== 0) return false;
  return BEST_EFFORT_THIRD_PARTY_URLS.some((pattern) => pattern.test(url));
}

/**
 * The app's deliberate keep-warm "wake knock" (client/src/config/wakeKnock.ts,
 * WAKE_URL_MARKER = 'wake=1'). The knock is an OPTIONS fetch that Chrome ALWAYS
 * rejects with a CORS error after the server has answered the preflight 204 —
 * the server answer is what wakes the function, so the status-0 failure the page
 * sees is expected on every knock, not a transport failure. Counting it made
 * three page loads look like a callable outage (OFFICEHUB-CLIENT-75).
 *
 * The marker is duplicated here rather than imported: wakeKnock.ts imports the
 * Firebase config, which this module must not pull in. If the knock's method or
 * marker changes, change this matcher with it.
 *
 * Narrow on purpose: status 0 only, method OPTIONS only, our callables origin
 * only (anchored like OUR_CALLABLE_URL), and an exact `wake=1` query parameter
 * (not `wake=10` / `awake=1`). A real callable POST, any HTTP 5xx, or an
 * unmarked URL is still counted, so a genuine CORS/IAM break stays loud.
 */
const WAKE_KNOCK_URL =
  /^https?:\/\/[a-z0-9-]+-quoter-saas\.cloudfunctions\.net\/[A-Za-z0-9_]+\?(?:[^#]*&)?wake=1(?:[&#]|$)/i;

function isExpectedWakeKnockFailure(url: string, status: number, method: string): boolean {
  if (status !== 0) return false;
  if (method.toUpperCase() !== 'OPTIONS') return false;
  return WAKE_KNOCK_URL.test(url);
}

/**
 * The background build-version poll, `useAppVersionCheck` -> `/version.json`.
 *
 * Same shape as FIRE_AND_FORGET_TELEMETRY_URLS above and deliberately NOT in
 * IGNORED_ERROR_URLS: that list suppresses a URL at EVERY status, which would
 * be exactly wrong here. A 5xx on `version.json` that KEEPS happening means
 * the published build stamp is broken, and `useAppVersionCheck` swallows every
 * failure in a bare `catch {}` with no counter — so a broken version.json is
 * indistinguishable from "no update available", silently, for every user in the
 * company. That is the single most valuable signal this endpoint can produce
 * and it must never be suppressed: status 0 is dropped, and a 5xx is reported
 * once it has repeated (see reportVersionPollFailure — a single 5xx is a
 * hosting blip, Sentry OFFICEHUB-CLIENT-68).
 *
 * A 404 (the stamp is MISSING — a deploy lost it) joins the same streak; it
 * is the only 4xx that does, and only for this URL. Every other 4xx on every
 * other URL is still dropped.
 *
 * Status 0 on this URL genuinely carries nothing: the poll fires every 5
 * minutes in every open tab plus on `focus` and `online`, so a backgrounded
 * phone, a tab being torn down, or a walk out of coverage produces a request
 * that never left the device. The caller retries on the next tick by design.
 *
 * Anchored to the app's OWN ROOT-SERVED file: an optional scheme+host, then the
 * path exactly `/version.json`, then end, query or hash. That matches both the
 * relative URL the client actually requests (`url = '/version.json'`) and the
 * absolute published one, while `/api/version.json.bak`, `/manifest.json`,
 * `/assets/version.json` and `https://other.example/x/version.json` do not
 * borrow the suppression.
 *
 * Root-anchoring is what makes it same-origin ENOUGH to be safe: the poll only
 * ever requests its own origin's root file, and gen-version.mjs publishes
 * `/version.json` at the root of all four apps (verified against the four live
 * hosts). A nested `version.json` is therefore some OTHER file that happens to
 * share a name — most plausibly a build asset — and its failures are not this
 * poll's known-benign teardown noise.
 *
 * NOT host-scoped beyond that, deliberately: the four apps are served from four
 * different hosts, so pinning one would silently stop matching. The residual
 * gap is a third-party that serves a ROOT `version.json`; the client never
 * requests one, and the failure direction is the safe one — an unmatched URL
 * becomes NOISY, never silent.
 */
const VERSION_POLL_URL = /^(?:https?:\/\/[^/]+)?\/version\.json(?:[?#]|$)/i;

/**
 * True when the version poll failed at the network layer (status 0). Every
 * other status, including every 4xx and 5xx, still reports — that is the
 * control which keeps a broken or missing version.json visible.
 */
function isVersionPollTransientFailure(url: string, status: number): boolean {
  if (status !== 0) return false;
  return VERSION_POLL_URL.test(url);
}

// ── Environmental status-0 noise (A20 / network-abort-noise) ───────────────
//
// Sentry stage 2a/2b sampled 30 "Network error" (status 0) events against the
// server's own logs: the server received and 200'd almost all of them. Two
// distinct, non-actionable causes accounted for most of the sample:
//
//   (a) PAGE UNLOAD/RELOAD tearing down an in-flight callable. 17 of 30
//       sampled search-key events fired 2-9s after the user clicked Update,
//       2-6s into the fetch — applyUpdate's `window.location.reload()`
//       killed the request mid-flight. Nothing is wrong; the tab just left.
//   (b) DEVICE ASLEEP/OFFLINE. Client-measured durations from 135s to 16h,
//       with the retry succeeding on the very next tick. A phone that
//       suspended mid-request reports the eventual OS-level abort the same
//       way a real network fault would.
//
// Both are status-0-only: a 5xx means the server actually responded with a
// fault, which is real signal regardless of what the tab or device was doing,
// so none of the three checks below ever apply above status 0.

/**
 * True once this tab has started unloading (navigating away, reloading, or
 * closing). Set from `pagehide`/`beforeunload`, which the browser fires
 * BEFORE it tears down in-flight requests — that ordering is what lets this
 * flag win the race and mark the failure before `wrappedFetch`'s catch block
 * (contextCapture.ts) even runs. `pagehide` is the primary signal (also fires
 * on bfcache entry); `beforeunload` is kept as a backstop for the small set of
 * cases where a browser fires only one of the two.
 *
 * Deliberately NOT driven by `visibilitychange` alone AS A SETTER — a tab
 * going `hidden` happens on every tab-switch and app-backgrounding and is not
 * unload at all; treating it as unload would suppress a real fault every time
 * the user merely switched tabs mid-request. `visibilitychange` IS used below
 * as a CLEARER, which is the opposite direction and doesn't have that problem.
 *
 * 🔴 THIS FLAG MUST BE CLEARABLE, OR IT PERMANENTLY SILENCES THE TAB.
 * `pagehide`/`beforeunload` both fire in cases where the document does NOT
 * actually go away and the JS context (this module's state included) lives
 * on:
 *   - bfcache: `pagehide` fires with `event.persisted === true`, the page is
 *     frozen rather than destroyed, and the user can come straight back via
 *     Back/Forward — `pageshow` then fires with `event.persisted === true`
 *     on the SAME module instance.
 *   - a cancelled `beforeunload` (the browser's native "Leave site?" dialog,
 *     user picks Stay): the navigation never happens and the tab is exactly
 *     as before, but nothing else ever un-sets the flag.
 * Without a clearer, either case leaves `pageIsUnloading` stuck `true` for
 * the rest of the tab's life, and every later REAL status-0 failure goes
 * silently unreported — the opposite failure from the one this file exists to
 * fix. Two independent clearers cover both cases: `pageshow` with
 * `event.persisted` (the bfcache-restore signal) and `visibilitychange`
 * back to `visible` (fires when a cancelled-navigation tab regains focus,
 * and is otherwise a no-op here since it only ever clears, never sets).
 */
let pageIsUnloading = false;

function markPageUnloading(): void {
  pageIsUnloading = true;
}

function clearPageUnloading(): void {
  pageIsUnloading = false;
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', markPageUnloading);
  window.addEventListener('beforeunload', markPageUnloading);
  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) clearPageUnloading();
  });
}
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') clearPageUnloading();
  });
}

/**
 * Test-only: restore pristine unload state. `pageIsUnloading` is a module
 * singleton exactly like the throttle state above, and a real `pagehide`
 * dispatch in one test would otherwise leak into every test that runs after
 * it in the same file/worker.
 */
export function __resetPageUnloadStateForTests(): void {
  pageIsUnloading = false;
}

function isPageUnloadOrReload(status: number): boolean {
  return status === 0 && pageIsUnloading;
}

/**
 * True when the browser itself reports no connection at the moment of
 * failure. `navigator.onLine` is a coarse, best-effort signal (it can read
 * `true` on a captive portal with no real route out), but a `false` reading
 * is reliable: the platform would not report it without cause, and a status-0
 * fetch failure while genuinely offline carries no signal the app can act on.
 */
function isDeviceOffline(status: number): boolean {
  return status === 0 && typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * The largest callable timeout explicitly configured anywhere in client/src
 * today (`huddle_generateBriefing`, `BRIEFING_CALLABLE_TIMEOUT_MS` in
 * useGenerateBriefing.ts) is 540_000ms (9 minutes); every callable that does
 * not override the SDK default times out at 70s. So no legitimate callable
 * round trip — success, timeout, or SDK-level failure — should ever measure
 * more than 9 minutes end to end.
 *
 * The measured device-sleep cluster ran 135s to 16h, i.e. it starts well
 * below 9 minutes and runs orders of magnitude past it. A threshold picked
 * too low (e.g. 120s, comfortably inside the 135s-16h cluster but also inside
 * the 540s briefing callable's normal range) would misclassify a genuinely
 * slow — but real — 540s callable timeout as device sleep and suppress it,
 * which is exactly the failure this module exists to avoid (a status-0
 * failure at normal duration must still report). 600_000ms (10 minutes) sits
 * comfortably above every known legitimate callable duration while still
 * catching the low end of the measured sleep cluster; it intentionally does
 * NOT catch the whole 135s-16h cluster (a short 135s-599s gap is expected to
 * be caught instead by the offline/unload checks above, or — if neither
 * applies — to still report, which is the safe direction for an ambiguous
 * case).
 *
 * `wrappedFetch` (contextCapture.ts) wraps every `window.fetch` call the app
 * makes, so this threshold governs all of them uniformly; large file/photo
 * uploads go through Firebase Storage's XHR-based SDK rather than `fetch`, so
 * they never hit this path, and no legitimate >10-minute `fetch` is expected
 * to exist today — if one is ever added, under-suppression (still reporting)
 * is the failure direction this threshold defaults to, not the reverse.
 */
const SLEEP_SUSPENSION_THRESHOLD_MS = 600_000;

function isConsistentWithDeviceSleep(status: number, duration: number): boolean {
  return status === 0 && duration > SLEEP_SUSPENSION_THRESHOLD_MS;
}

/**
 * Path segments that are identifiers rather than route names.
 *
 * 🔴 THE LENGTH-ONLY RULE THAT USED TO LIVE HERE WAS WRONG FOR THIS APP, and
 * wrong in the exact direction this whole module exists to fix. It collapsed any
 * segment of 20+ characters, but a Firebase callable URL is
 * `https://<region>-quoter-saas.cloudfunctions.net/<functionName>` — the
 * function name IS the only path segment, and this app's names are long.
 * Measured against the real call sites (`httpsCallable(...)` across client/src):
 * **211 of 279 distinct callable names are >= 20 characters**, so 76% of the
 * app's network errors would have merged into a single
 * `POST|500|.../:id` bucket. That is the everything-in-one-bucket defect,
 * reintroduced by the fix for it.
 *
 * Note `acceptStageSuggestion` (21 chars, no underscore) — an underscore-based
 * exemption would not have caught it either. The discriminator that works is
 * SHAPE, not length: a Firestore auto-ID is exactly 20 characters of mixed
 * alphanumerics and in practice contains a digit; a route name does not look
 * like that. Verified against the real list: 0 of 279 callable names match the
 * rules below, while `12345`, `8f3c9a1b2d4e5f60`, a UUID, and a 20-char
 * auto-ID all do.
 *
 * Where the rules are uncertain they fail toward FRAGMENTING (more issues), not
 * MERGING. An over-split issue list is untidy; an over-merged one hides
 * failures, which is the bug being fixed.
 */
const ID_SEGMENT_RULES: readonly RegExp[] = [
  /^\d+$/,                       // numeric id
  /^[0-9a-f]{8,}$/i,             // hex token / short hash
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // uuid
  /^(?=.*\d)[A-Za-z0-9]{20}$/,   // Firestore auto-ID: exactly 20 alphanumerics, containing a digit
];

/**
 * The stable part of a URL, for grouping only.
 *
 * Drops the query string and hash, and collapses id-shaped path segments per
 * ID_SEGMENT_RULES above. Both halves matter and they pull in opposite
 * directions:
 *
 *   - Keep the query and every failing request to one endpoint becomes its own
 *     Sentry issue. That is the SAME defect as everything-in-one-bucket, just
 *     inverted — an issue list nobody can read either way.
 *   - Collapse too eagerly and two genuinely different endpoints merge.
 *
 * `/api/tickets/8f3c.../photos` groups with `/api/tickets/91ab.../photos`;
 * `/api/tickets` and `/api/invoices` never merge.
 *
 * ⚠️ TRUST BOUNDARY: the result goes into the Sentry fingerprint, which is
 * issue-title-visible. It is NOT a sanitised value — an 8-19 character opaque
 * token in a path (a share slug, a short session id) survives verbatim. That is
 * the same trust boundary as `additionalContext.url` below, which already
 * carries the full URL including its query string; this is not a safer copy, it
 * is a more visible one. Do not put a URL here that you would not put there.
 *
 * Exported for the test that proves both directions (same endpoint groups,
 * different endpoints do not).
 */
function parseUrlForGrouping(url: string): { origin: string; pathname: string } {
  // Parsed with the platform's URL rather than a hand-rolled regex, because a
  // wrong parser here is invisible — it would just quietly group the wrong
  // things. The absolute/relative split is explicit so a relative URL never
  // borrows a synthetic origin: only the PATH of a relative URL is meaningful,
  // and an absolute one must keep its real host so a third-party failure cannot
  // group with one of ours.
  const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
  const isProtocolRelative = !isAbsolute && url.startsWith('//');

  // Everything before the first `?` or `#`. Also the fallback if parsing fails,
  // so a malformed URL still cannot be fragmented by its query string.
  let pathname = url.split(/[?#]/)[0];
  let origin = '';

  try {
    if (isProtocolRelative) {
      // `//cdn.example.com/x` carries a HOST but no scheme. Routing it through
      // the relative branch would read only its path and silently discard the
      // host, merging two unrelated third-party hosts into one group — the same
      // over-merge defect as the length rule, by a different door.
      const parsed = new URL(`https:${url}`);
      origin = `//${parsed.host}`; // scheme is ours, not the caller's — don't assert it
      pathname = parsed.pathname;
    } else if (isAbsolute) {
      const parsed = new URL(url);
      origin = parsed.origin;
      pathname = parsed.pathname;
    } else {
      // The base exists only to make a relative reference parseable; its origin
      // is deliberately discarded. `.invalid` is reserved by RFC 2606 and can
      // never resolve to a real host.
      pathname = new URL(url, 'http://relative.invalid').pathname;
    }
  } catch {
    // Malformed enough that the platform parser rejects it (e.g. `http://`).
    // The split above already removed the query; group on what is left.
  }

  return { origin, pathname };
}

export function fingerprintNetworkUrl(url: string): string {
  const { origin, pathname } = parseUrlForGrouping(url);

  const collapsed = pathname
    .split('/')
    .map((segment) =>
      segment && ID_SEGMENT_RULES.some((rule) => rule.test(segment)) ? ':id' : segment
    )
    .join('/');

  return `${origin}${collapsed}`;
}

/**
 * The HOST a request was trying to reach — the grouping key for a transport
 * failure, where the endpoint is not the cause. Empty string for a relative
 * URL, which means "this app's own origin" and is a stable key like any other.
 *
 * Shares `parseUrlForGrouping` with `fingerprintNetworkUrl` on purpose: a
 * second, subtly-different URL parser would not throw or log, it would just
 * quietly group the wrong things — the same invisible-failure argument that
 * comment makes about using the platform parser in the first place.
 */
function networkHost(url: string): string {
  return parseUrlForGrouping(url).origin;
}

/**
 * The Sentry grouping key for a network failure. The status decides which of
 * two genuinely different questions is being asked.
 *
 * **status >= 500 — the server RESPONDED with a fault.** The endpoint is the
 * identity of the failure: `/billing_createInvoice` returning 500 is a
 * different bug from `/forms_createSubmission` returning 500, and merging them
 * is the catch-all defect #4666 fixed. Unchanged from #4666.
 *
 * **status 0 — there was no HTTP response at all.** The request died in
 * transport: tab unload, dead radio, suspended device, a broken route to a
 * host. The endpoint is a coincidence of whatever happened to be in flight, not
 * a cause — #4761's server-log sampling found the server had received and 200'd
 * almost all of them. Fingerprinting those per endpoint does not reveal 39
 * failures, it manufactures 39 issues out of one, and each then needs its own
 * dismissal. Measured on OFFICEHUB-CLIENT-2, the ignored catch-all this whole
 * line of work exists to unblind: 39 distinct endpoints inside it, **all of
 * them status 0, not one HTTP 5xx.** Re-derive rather than trust that number:
 *
 *     node scripts/facts/sentry-recurrence.mjs --days 120
 *
 * So status 0 groups by HOST — which is what the transport actually failed to
 * reach, and what distinguishes "our callables are unreachable" from
 * "Nominatim is down". Method is kept because a failed GET for an asset and a
 * failed POST of a callable are different enough to triage apart, and it adds
 * at most a handful of buckets rather than one per endpoint.
 *
 * 🔴 WHAT THIS DELIBERATELY GIVES UP, AND HOW IT IS PAID BACK. A single
 * endpoint genuinely broken at the transport layer — a CORS regression, a
 * dropped IAM invoker binding — presents as status 0 and no longer gets its own
 * issue. That is a real loss and it is covered by a TAG, not by hope:
 * `network_endpoint` is set on every network event, so the host-level issue's
 * own tag distribution names the endpoints behind it. One endpoint at 95% of a
 * transport bucket is visible on the issue page; the same signal spread across
 * 39 issues is not visible anywhere.
 */
function networkFingerprint(url: string, status: number, method: string): readonly string[] {
  if (status === 0) {
    return ['network_request', 'transport-failure', method, networkHost(url)];
  }
  return ['network_request', method, String(status), fingerprintNetworkUrl(url)];
}

/**
 * The callable NAME when this URL is one of OUR Cloud Functions, else null.
 *
 * Host-scoped to this project's functions origin with the region as a wildcard,
 * for the same reasons spelled out on FIRE_AND_FORGET_TELEMETRY_URLS above:
 * callables are split across us-central1 and us-west1, and pinning one region
 * would silently stop matching if a function moved.
 *
 * FAILURE DIRECTION, and it is the safe one: if the origin ever changes (a
 * custom domain via REACT_APP_FUNCTIONS_URL, a project rename) this stops
 * matching, the counter-backed suppression below stops applying, and these
 * failures become NOISY again — never silent.
 *
 * 🔴 ANCHORED AT THE START OF THE URL, not merely "contains". An unanchored
 * `//host/name` matches anywhere in the string, so
 * `https://evil.example/?redirect=//us-central1-quoter-saas.cloudfunctions.net/forms_createSubmission`
 * would be classified as ours and borrow this suppression. Today the only
 * caller passes a URL the app itself requested, so that is not reachable — but
 * a suppression rule that depends on its call site staying first-party-only is
 * one refactor away from being a blind spot, and the anchor costs nothing.
 * Only the function NAME is captured, never the query string, so no id or
 * token can ride into the stored endpoint or the Sentry tag.
 */
const OUR_CALLABLE_URL = /^https?:\/\/[a-z0-9-]+-quoter-saas\.cloudfunctions\.net\/([A-Za-z0-9_]+)(?:[?#/]|$)/i;

function ourCallableName(url: string): string | null {
  const match = OUR_CALLABLE_URL.exec(url);
  return match ? match[1] : null;
}

/**
 * The loud report a BURST of status-0 failures on one callable earns.
 *
 * This is the whole justification for suppressing the individual events: the
 * shape that matters — one callable, repeatedly, in a short window — now gets a
 * BETTER report than it used to, not a worse one. It names the callable
 * (instead of a URL with a cache-buster that fragmented the group), carries the
 * window and day counts, and groups per callable in both sinks, so a dropped
 * IAM invoker binding shows up as one climbing group across many users.
 *
 * Deliberately NOT gated on `shouldReport`: its own cooldown
 * (TRANSPORT_BURST_WINDOW_MS, per callable) is its rate limit, and the global
 * 10-per-minute budget is spent by exactly the high-volume noise this
 * escalation exists to see through. Being throttled out by the noise would
 * defeat the purpose.
 *
 * 🔴 LIMIT: `recordTransportFailure` (transportFailureCounter.ts) is a
 * per-browser-tab counter with no cross-user aggregation, so this only fires
 * when ONE tab hits 3 failures on the same callable within the window. A
 * brief, widespread outage where every affected tab only sees 1-2 failures
 * never trips it — no tab escalates, so this loud report never fires, even
 * though many users were affected. Every event still reaches Sentry either
 * way (`Sentry.captureException` in captureError.ts runs before its
 * `skipErrorGroups` early-return), so Sentry's host-level transport bucket
 * is the only cross-user signal for that shape.
 */
function reportTransportBurst(
  endpoint: string,
  method: string,
  windowCount: number,
  dayCount: number
): void {
  captureError({
    error: new Error(
      `Callable transport failures: ${windowCount} on ${endpoint} within 10 minutes (${dayCount} today)`
    ),
    severity: 'error',
    category: 'network_request',
    capturePoint: 'network_interceptor',
    fingerprint: ['network_request', 'transport-burst', endpoint],
    tags: { network_endpoint: endpoint, transport_burst: 'true' },
    additionalContext: { endpoint, method, status: 0, windowCount, dayCount },
  });
}

/**
 * A failed version poll (5xx, or 404 = stamp missing). Counted; reported ONCE per streak, when the
 * streak reaches VERSION_POLL_STREAK_THRESHOLD, as a warning with a stable
 * fingerprint — a broken build stamp shows up as one group gaining users,
 * while a single hosting blip is not reported at all (Sentry OFFICEHUB-CLIENT-68).
 * Not gated on `shouldReport`: one report per streak is its own rate limit.
 */
function reportVersionPollFailure(url: string, status: number, method: string, duration: number): void {
  const { consecutive, report } = recordVersionPollFailure();
  if (!report) return;
  captureError({
    error: new Error(`version.json poll failing: ${consecutive} consecutive failures (latest HTTP ${status})`),
    severity: 'warning',
    category: 'network_request',
    capturePoint: 'network_interceptor',
    fingerprint: ['network_request', 'version-poll-streak'],
    tags: { network_endpoint: fingerprintNetworkUrl(url), version_poll_streak: 'true' },
    additionalContext: { url, method, status, duration, consecutive },
  });
}

/**
 * Called by contextCapture when fetch returns an ok response. Only the version
 * poll cares today: a successful poll ends its failure streak.
 */
export function handleNetworkSuccess(url: string): void {
  if (VERSION_POLL_URL.test(url)) recordVersionPollSuccess();
}

/** Breadcrumb category for a hedged QuickBooks read (lib/callableRetry.ts); the post-deploy check reads `attempt` and `elapsedMs`. */
export const CALLABLE_HEDGE_CATEGORY = 'callable-hedge';

/**
 * Called by contextCapture when a hedged read that sent a second attempt settles. One breadcrumb in this app's error reports and one in
 * Sentry, carrying the callable NAME, the answering attempt (1, 2, or 0 when neither answered), the elapsed ms, why attempt 2 was sent, and the wake context (tab visibility, ms since this browser's last knock for it).
 * Never the URL's query, the payload or the answer.
 */
export function handleCallableHedge(outcome: HedgeOutcome): void {
  const attempt = outcome.answeredBy ?? 0;
  const data = { callable: outcome.callable, attempt, elapsedMs: Math.round(outcome.elapsedMs), trigger: outcome.trigger, ...wakeContextFor(outcome.callable) };
  const message =
    attempt === 0
      ? `${outcome.callable}: no answer from either attempt after ${data.elapsedMs} ms`
      : `${outcome.callable}: answered by attempt ${attempt} after ${data.elapsedMs} ms`;
  addBreadcrumb('api_response', CALLABLE_HEDGE_CATEGORY, message, data);
  Sentry.addBreadcrumb({ category: CALLABLE_HEDGE_CATEGORY, level: attempt === 0 ? 'warning' : 'info', message, data });
}

/**
 * Called by contextCapture when fetch returns a non-ok response or network failure.
 * Reports 5xx errors and network failures; skips 401/403 (expected auth redirects)
 * and SDK-managed endpoints (Firestore, Storage, Auth) where the SDK handles retries.
 */
export function handleNetworkError(
  url: string,
  status: number,
  method: string,
  duration: number,
  responseBody?: string
): void {
  // Skip auth-related status codes (expected)
  if (status === 401 || status === 403) return;
  // The ONE 4xx that counts: a 404 on our own build stamp means a deploy lost
  // it and every client's update check is blind. It joins the 5xx streak.
  if (status === 404 && VERSION_POLL_URL.test(url)) {
    reportVersionPollFailure(url, status, method, duration);
    return;
  }
  // A 4xx from one of OUR callables is a deliberate server refusal. It never creates an error report, but it is COUNTED per
  // callable + status (ISS-460: every save refused for four days, no sound). Other 4xx stay ignored.
  if (status >= 400 && status < 500) {
    const refused = ourCallableName(url);
    if (refused) reportCallableHttpRejection(refused, status);
    return;
  }
  // Only report server errors (5xx) and network failures (0)
  if (status > 0 && status < 500) return;
  // Skip SDK-managed endpoints where we only add noise, never signal
  if (isIgnoredUrl(url)) return;
  // A 5xx/network failure that withTransientRetry will retry is not yet anyone's failure (#4630): a breadcrumb now,
  // and the last attempt reports as usual, so only a retry budget that runs out becomes a Sentry error.
  if (deferToRetry(url, status, method, duration, responseBody)) return;
  // A background draft-sync tick failing at the network layer is EXPECTED in
  // the field and owned by useFormDraftSync (it retries next tick, and
  // surfaces a persistent notice on permanent refusal). Only status-0 is
  // suppressed, and only while a tick is in flight — user-initiated saves and
  // submits, and every HTTP 5xx, keep full reporting so a lost IAM binding or
  // CORS break still surfaces (OFFICEHUB-CLIENT-57).
  if (isDraftSyncTransientNetworkFailure(url, status)) return;
  // A fire-and-forget telemetry beacon that never left the device. Its caller
  // already handles and counts the drop; only status-0 is suppressed, so a
  // broken telemetry function (5xx) still surfaces (OFFICEHUB-CLIENT-2 / -J).
  if (isFireAndForgetTelemetryNetworkFailure(url, status)) return;
  // A best-effort third-party lookup that never got a response; its caller
  // already falls back (OFFICEHUB-CLIENT-6H). Status-0 only.
  if (isBestEffortThirdPartyNetworkFailure(url, status)) return;
  // The deliberate keep-warm OPTIONS knock (`?wake=1`) is always CORS-rejected
  // by the browser after the server answers; it must never be counted as a
  // callable transport failure (OFFICEHUB-CLIENT-75). Status-0 OPTIONS only.
  if (isExpectedWakeKnockFailure(url, status, method)) return;
  // The background build-version poll never left the device. Only status-0 is
  // suppressed, so a broken or missing version.json (4xx/5xx) still surfaces —
  // see the comment block on VERSION_POLL_URL for why that matters more here
  // than for any other endpoint.
  if (isVersionPollTransientFailure(url, status)) return;
  // A version-poll 5xx is reported only as a STREAK — see reportVersionPollFailure.
  if (VERSION_POLL_URL.test(url)) {
    reportVersionPollFailure(url, status, method, duration);
    return;
  }
  // The page itself is unloading/reloading — see the A20 comment block above.
  if (isPageUnloadOrReload(status)) return;
  // The device has no connection right now — same block.
  if (isDeviceOffline(status)) return;
  // The round trip's duration is only consistent with the device having been
  // asleep/suspended for the gap, not with any real callable completing.
  if (isConsistentWithDeviceSleep(status, duration)) return;

  // A status-0 failure on one of OUR callables: no HTTP response at all, so the
  // endpoint is a coincidence of what was in flight rather than a cause. These
  // stop minting one `error_groups` document per callable — but they are
  // COUNTED first, unconditionally, and a burst escalates into a real report.
  // Counting happens BEFORE the throttle below on purpose: a count lost to a
  // rate limiter is a silent drop, which is the thing this must never become.
  const transportEndpoint = status === 0 ? ourCallableName(url) : null;
  if (transportEndpoint) {
    const outcome = recordTransportFailure(transportEndpoint);
    if (outcome.escalate) {
      reportTransportBurst(transportEndpoint, method, outcome.windowCount, outcome.dayCount);
      return;
    }
  }

  // A third party's query string can carry the device's GPS or a search term;
  // only our own endpoints keep theirs (see reportableUrl).
  const shownUrl = reportableUrl(url);
  const ourCallable = ourCallableName(url);
  const message = status === 0
    ? `Network error: ${method} ${shownUrl}`
    : `HTTP ${status}: ${method} ${shownUrl}`;

  if (!shouldReport(message)) return;
  markReported(message);

  captureError({
    error: new Error(message),
    severity: 'error',
    category: 'network_request',
    capturePoint: 'network_interceptor',
    // The Error above is synthesised HERE, on this line, for every endpoint in
    // the app — so its stack is identical every time and Sentry's frame-based
    // grouping puts a failing invoice callable, a failing Typesense query and a
    // failing storage upload in ONE issue. Resolving that issue silently
    // "resolves" endpoints nobody looked at. Endpoint + status + method is the
    // grouping that matches how these are actually triaged.
    //
    // The `message` above is deliberately left UNCHANGED (full URL, query string
    // and all): it is the key `shouldReport`/`markReported` dedupe on, and
    // narrowing it would start collapsing reports that fire today. This changes
    // GROUPING only — not one event is dropped.
    fingerprint: networkFingerprint(url, status, method),
    // The endpoint, ALWAYS — indexed and filterable, and the thing that keeps a
    // host-level transport bucket diagnosable. `additionalContext.url` below
    // carries the same information but lands in Sentry's `extra`, which is not
    // indexed, not filterable and not aggregated into a distribution: you can
    // read it on one event, never ask "which endpoints are in this issue".
    // Deliberately the COLLAPSED url (no query string, ids replaced) — a tag is
    // a low-cardinality dimension, and the raw url would make it useless as one.
    //
    // A failed forms callable also names WHICH report it was (submission,
    // template and ticket ids — never answers or names), so "did this save later
    // reach the server?" can be answered by id (OFFICEHUB-CLIENT-63). The
    // fingerprint above is untouched: grouping does not change.
    tags: { network_endpoint: fingerprintNetworkUrl(url), ...activeFormTagsForUrl(url) },
    // Sentry keeps this class whole (one host-level transport bucket, endpoint
    // tags); `error_groups` would fragment it into one document per callable,
    // each needing its own dismissal. The drop is counted and a burst escalates
    // — see `skipErrorGroups` in types.ts for why that pair is the contract.
    ...(transportEndpoint ? { skipErrorGroups: true } : {}),
    additionalContext: {
      url: shownUrl,
      method,
      status,
      duration,
      ...(ourCallable ? wakeContextFor(ourCallable) : {}),
      ...reportableResponseBody(url, status, responseBody),
    },
  });
}

/** Hard cap on an attached response body, in characters. */
const MAX_REPORTED_BODY_CHARS = 1024;

/**
 * The 5xx body to attach to a network error report (#3721), or nothing.
 *
 * Our onRequest functions return the root cause in the body, but the report used to
 * carry only url/method/status/duration, so every function 500 arrived with no cause.
 * Attached ONLY when all three hold: a 5xx (never a 4xx or a status-0 failure), from
 * our OWN functions origin (a third party's body is not ours to ship), and non-empty.
 * Always scrubbed (emails, phones, tokens, card/SSN, sensitive keys) and capped at 1KB.
 */
function reportableResponseBody(
  url: string,
  status: number,
  responseBody: string | undefined
): { responseBody?: string } {
  if (!responseBody || !shouldCaptureNetworkErrorBody(url, status)) return {};
  try {
    return { responseBody: redactForErrorReport(responseBody, MAX_REPORTED_BODY_CHARS) };
  } catch {
    // A scrubber failure omits the BODY only; the url/status/method report still ships.
    // Never fall back to the raw text.
    return {};
  }
}

/**
 * Whether a failed response's body may be read for error reporting at all: a 5xx from
 * one of OUR functions (#3721). contextCapture asks this BEFORE reading, so a third
 * party's body, a 4xx body and a status-0 failure are never even read.
 */
export function shouldCaptureNetworkErrorBody(url: string, status: number): boolean {
  return status >= 500 && ourCallableName(url) !== null;
}

// A failure held back for a retry that never came is reported through the normal path (retryWindow.ts, #4630).
setDeferredFailureReporter((f) => handleNetworkError(f.url, f.status, f.method, f.duration, f.responseBody));
